import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import api from '../../utils/api';

// Works for both create (no `order` prop) and edit (pass existing `order` object)
export default function OrderModal({ customerId, customerName, order, onClose, onSaved }) {
  const isEdit = !!order;

  const [notes, setNotes] = useState(order?.notes || '');
  const [totalAmount, setTotalAmount] = useState(order?.total_amount ?? '');
  const [items, setItems] = useState(() => {
    if (order?.items) {
      try {
        const parsed = Array.isArray(order.items) ? order.items : JSON.parse(order.items);
        return parsed.length > 0 ? parsed : [{ description: '', quantity: 1, price: '' }];
      } catch { return [{ description: '', quantity: 1, price: '' }]; }
    }
    return [{ description: '', quantity: 1, price: '' }];
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    api.get('/settings').then((res) => {
      const favs = res.data.favorite_items;
      if (Array.isArray(favs)) setFavorites(favs);
    }).catch(() => {});
  }, []);

  function addItem() {
    setItems((i) => [...i, { description: '', quantity: 1, price: '' }]);
  }

  function updateItem(i, key, val) {
    setItems((items) => items.map((item, idx) => idx === i ? { ...item, [key]: val } : item));
  }

  function removeItem(i) {
    setItems((items) => items.filter((_, idx) => idx !== i));
  }

  function addFavoriteToOrder(fav) {
    const newItem = { description: fav.name, quantity: 1, price: fav.price ?? '' };
    setItems((prev) => {
      const last = prev[prev.length - 1];
      if (prev.length === 1 && !last.description.trim() && !last.price) {
        return [newItem];
      }
      return [...prev, newItem];
    });
  }

  const computedTotal = items.reduce((sum, item) => {
    const p = parseFloat(item.price) || 0;
    const q = parseInt(item.quantity) || 0;
    return sum + p * q;
  }, 0);

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const validItems = items.filter((i) => i.description.trim());
      const total = totalAmount !== '' ? parseFloat(totalAmount) : (computedTotal || null);
      if (isEdit) {
        await api.put(`/orders/${order.id}`, { notes, items: validItems, total_amount: total });
      } else {
        await api.post('/orders', { customer_id: customerId, notes, items: validItems, total_amount: total });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשמירת הזמנה');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {isEdit ? `עריכת הזמנה #${order.order_number}` : 'הזמנה חדשה'}
            </h2>
            {(customerName || order?.customer_name) && (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                ללקוח: {customerName || order.customer_name}
              </p>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">פריטים</label>
              <button type="button" onClick={addItem}
                className="flex items-center gap-1 text-xs text-primary-600 dark:text-primary-400 hover:underline">
                <Plus size={12} /> הוסף פריט
              </button>
            </div>
            {favorites.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {favorites.map((fav, i) => (
                  <button key={i} type="button" onClick={() => addFavoriteToOrder(fav)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium
                               bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300
                               border border-primary-200 dark:border-primary-700 hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors">
                    + {fav.name}{fav.price != null ? ` ₪${fav.price.toLocaleString()}` : ''}
                  </button>
                ))}
              </div>
            )}
            <div className="space-y-2">
              {items.map((item, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input
                    value={item.description}
                    onChange={(e) => updateItem(i, 'description', e.target.value)}
                    placeholder="תיאור פריט"
                    className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                               bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => updateItem(i, 'quantity', e.target.value)}
                    min={1}
                    className="w-16 px-2 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                               bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-center"
                    placeholder="כמות"
                  />
                  <input
                    type="number"
                    value={item.price}
                    onChange={(e) => updateItem(i, 'price', e.target.value)}
                    placeholder="₪"
                    className="w-24 px-2 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                               bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    dir="ltr"
                  />
                  {items.length > 1 && (
                    <button type="button" onClick={() => removeItem(i)}
                      className="p-1.5 text-gray-400 hover:text-red-500">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {computedTotal > 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 text-left" dir="ltr">
                סה"כ מחושב: ₪{computedTotal.toLocaleString()}
              </p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              סה"כ להדפסה (ידני, אם שונה מהמחושב)
            </label>
            <input
              type="number"
              value={totalAmount}
              onChange={(e) => setTotalAmount(e.target.value)}
              placeholder={computedTotal > 0 ? String(computedTotal) : '₪'}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                         bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              dir="ltr"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">הערות</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600
                         bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
              ביטול
            </button>
            <button type="submit" disabled={saving}
              className="flex-1 px-4 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-60">
              {saving ? 'שומר...' : (isEdit ? 'שמור שינויים' : 'פתח הזמנה')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
